import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  abrirRepeticao, adotarRepeticao, desistirRepeticao, verRepeticao,
} from '../../api/repeticao';
import type { EstadoDaRepeticao, FichaDaRepeticao } from '../../api/repeticao';
import { Area, Aviso, Carregando, Dialogo, ErroDaApi } from '../../ui';

/**
 * A moldura da repetição na Interpretação (onda 323, RN-260, telas A–D).
 *
 * «O ensaio é repetido dentro da aba Interpretação, com os pontos novos
 *  lançados ali mesmo e comparados ao vivo com os iniciais» (D3). O termo é
 * **repetição** (D4). Durante ela o laudo continua com o resultado inicial;
 * só muda quando a repetição é adotada (D5).
 *
 * A moldura é a mesma para todas as fichas: o botão «Repetir o ensaio
 * aqui», a faixa âmbar com «Desistir da repetição», o selo «Repetindo ·
 * tentativa N», a comparação Inicial × Atual × Diferença, o lançamento e o
 * gráfico lado a lado, «Adotar a repetição» com a janela do motivo e, depois
 * de adotar, a faixa verde. Cada ficha só diz o que comparar, como lançar e
 * o que desenhar (render props que recebem o estado da repetição).
 *
 * Nada aqui decide: «podeAdotar» e as pendências vêm do servidor; o banco
 * recusa o que não pode, e a frase dele aparece na tela.
 */

// ══ o estado ══════════════════════════════════════════════════════════

export interface UseRepeticao<F = any> {
  dados: EstadoDaRepeticao<F> | null;
  carregando: boolean;
  erro: unknown;
  /** O número da tentativa adotada nesta visita (para a faixa verde). */
  adotada: number | null;
  /** Some com o erro (ao fechar a janela em que ele apareceu). */
  limparErro(): void;
  abrir(): Promise<boolean>;
  adotar(motivo: string): Promise<boolean>;
  desistir(motivo?: string): Promise<boolean>;
  recarregar(): Promise<void>;
}

/** De quanto em quanto a repetição aberta se relê sozinha (R3). */
export const RELER_A_CADA_MS = 10000;

/**
 * O estado da repetição de uma ficha.
 *
 * Com a repetição aberta, relê a cada 10 s enquanto a página está à vista
 * (`document.visibilityState`): o ponto que o laboratório lança na bancada
 * aparece na Interpretação de quem compara, sem recarregar a página (R3). O
 * que se digita na linha nova mora na ficha, e não aqui — não se perde.
 */
export function useRepeticao<F = any>(
  ensaioId: string, ficha: FichaDaRepeticao, aoMudar?: () => void | Promise<void>,
): UseRepeticao<F> {
  const [dados, setDados] = useState<EstadoDaRepeticao<F> | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<unknown>(null);
  const [adotada, setAdotada] = useState<number | null>(null);
  /*
   * Cada leitura leva um número; só a MAIS NOVA pode escrever o estado. Uma
   * volta do relógio que saiu antes de um «Gravar ponto» e chegou depois da
   * releitura dele traria a repetição de antes — e o ponto recém-gravado
   * sumiria da tela até a próxima volta (revisão final, achado 11).
   */
  const sequencia = useRef(0);
  const ler = useCallback(async () => {
    const minha = ++sequencia.current;
    const x = await verRepeticao(ensaioId, ficha) as EstadoDaRepeticao<F>;
    return { x, vale: minha === sequencia.current };
  }, [ensaioId, ficha]);

  const recarregar = useCallback(async () => {
    try {
      const { x, vale } = await ler();
      if (vale) { setDados(x); setErro(null); }
    } catch (e) { setErro(e); } finally { setCarregando(false); }
  }, [ler]);

  useEffect(() => { void recarregar(); }, [recarregar]);

  const aberta = dados?.aberta?.numero ?? null;
  useEffect(() => {
    if (aberta == null) return undefined;
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void ler()
          .then(({ x, vale }) => { if (vale) setDados(x); })
          .catch(() => { /* a próxima volta tenta de novo; o erro da ação fica */ });
      }
    }, RELER_A_CADA_MS);
    return () => window.clearInterval(t);
  }, [aberta, ler]);

  const agir = useCallback(async (chamada: () => Promise<unknown>) => {
    setErro(null);
    try {
      await chamada();
    } catch (e) { setErro(e); return false; }
    await recarregar();
    await aoMudar?.();
    return true;
  }, [recarregar, aoMudar]);

  return {
    dados, carregando, erro, adotada, recarregar, limparErro: () => setErro(null),
    abrir: () => { setAdotada(null); return agir(() => abrirRepeticao(ensaioId)); },
    adotar: async (motivo) => {
      const numero = dados?.aberta?.numero ?? null;
      const ok = await agir(() => adotarRepeticao(ensaioId, motivo.trim()));
      if (ok) setAdotada(numero);
      return ok;
    },
    desistir: (motivo) => agir(() => desistirRepeticao(ensaioId, motivo?.trim() || undefined)),
  };
}

// ══ a comparação ══════════════════════════════════════════════════════

/** Um lado da comparação: o número grande, a unidade e uma linha embaixo. */
export interface LadoDaComparacao {
  valor: ReactNode;
  unidade?: string;
  nota?: ReactNode;
}

export interface Comparacao {
  inicial: LadoDaComparacao;
  atual: LadoDaComparacao;
  diferenca: LadoDaComparacao;
  /**
   * A grandeza, quando a ficha compara mais de uma (a compactação: «w ótima»
   * e «ρd máx»). Entra no rótulo de cada lado; `chave` marca o grupo
   * (`data-grandeza`, por onde os testes leem).
   */
  rotulo?: string;
  chave?: string;
}

/**
 * Inicial · tentativa N × Atual · tentativa M (provisório) × Diferença.
 * `data-lado` marca cada número (os testes leem por ele).
 */
export function ComparacaoDaRepeticao({ vigente, numero, provisorio, comparacao }: {
  vigente: number; numero: number; provisorio: boolean; comparacao: Comparacao;
}) {
  const g = comparacao.rotulo;
  const lado = (chave: string, rotulo: string, l: LadoDaComparacao) => (
    <div className={`repeticao-lado repeticao-lado-${chave}`}>
      <small>{rotulo}</small>
      <span className="repeticao-numero n" data-lado={chave}>{l.valor}</span>
      {l.unidade && <span className="repeticao-unidade"> {l.unidade}</span>}
      {l.nota != null && <span className="repeticao-nota">{l.nota}</span>}
    </div>
  );
  return (
    <div className="repeticao-comparacao" role="group" data-grandeza={comparacao.chave}
      aria-label={g ? `Comparação · ${g}` : 'Comparação dos resultados'}>
      {lado('inicial', `${g ? `${g} · inicial` : 'Inicial'} · tentativa ${vigente}`,
        comparacao.inicial)}
      {lado('atual', `${g ? `${g} · atual` : 'Atual'} · tentativa ${numero}${provisorio ? ' (provisório)' : ''}`,
        comparacao.atual)}
      {lado('diferenca', g ? `${g} · diferença` : 'Diferença', comparacao.diferenca)}
    </div>
  );
}

/** «+3», «−2», «0» — a diferença com o sinal de menos tipográfico. */
export const comSinal = (n: number) => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : '0');

// ══ a moldura ═════════════════════════════════════════════════════════

type Pedaco<F> = ReactNode | ((r: UseRepeticao<F>) => ReactNode);
const desenhar = <F,>(p: Pedaco<F> | undefined, r: UseRepeticao<F>) =>
  (typeof p === 'function' ? (p as (r: UseRepeticao<F>) => ReactNode)(r) : p);

export interface MolduraDaRepeticaoProps<F = any> {
  ensaioId: string;
  ficha: FichaDaRepeticao;
  /** O nome do que se repete, no título da janela («o limite de liquidez»). */
  titulo: string;
  /**
   * O que comparar, a partir das duas leituras (inicial e atual) da ficha —
   * uma grandeza ou várias (a compactação compara w ótima e ρd máx). A ficha
   * que compara um quadro, e não números (as frações da granulometria), dá
   * `quadro` no lugar.
   */
  comparacao?(inicial: F, atual: F | null): Comparacao | Comparacao[];
  /** O quadro da comparação, no lugar dos números (granulometria). */
  quadro?: Pedaco<F>;
  /** O lançamento dos pontos novos (à esquerda). */
  lancamento: Pedaco<F>;
  /**
   * O gráfico inicial × atual (à direita) — ou, nas fichas que comparam só
   * números (D7), as linhas iniciais em leitura. Sem ele, o lançamento ocupa
   * a largura toda.
   */
  grafico?: Pedaco<F>;
  /** O que vem depois das duas colunas (ex.: os pontos iniciais em leitura). */
  depois?: Pedaco<F>;
  /** Quem não tem `ensaio.interpretar` não vê a moldura — só `children`. */
  podeInterpretar: boolean;
  /** Ficha congelada (aprovado, cancelado, substituído): não repete. */
  congelado?: boolean;
  /**
   * A frase da faixa âmbar, depois de «Repetindo o ensaio aqui mesmo.» — a
   * das fichas com gráfico é a padrão; as que comparam só números dizem a sua.
   */
  frase?: ReactNode;
  /** A Interpretação de sempre, mostrada quando não há repetição aberta. */
  children?: ReactNode;
  /** Depois de abrir, adotar ou desistir: a ficha recarrega. */
  aoMudar?(): void | Promise<void>;
  /** O estado, quando a ficha já o tem (o mesmo `useRepeticao`). */
  repeticao?: UseRepeticao<F>;
}

/**
 * A moldura. Usa o `repeticao` dado ou chama o `useRepeticao` ela mesma
 * (`MolduraComEstado`), para a ficha que precisa do estado fora dela.
 */
export function MolduraDaRepeticao<F = any>(props: MolduraDaRepeticaoProps<F>) {
  if (props.repeticao) return <MolduraComEstado {...props} r={props.repeticao} />;
  return <MolduraSemEstado {...props} />;
}

function MolduraSemEstado<F>(props: MolduraDaRepeticaoProps<F>) {
  const r = useRepeticao<F>(props.ensaioId, props.ficha, props.aoMudar);
  return <MolduraComEstado {...props} r={r} />;
}

function MolduraComEstado<F>({
  titulo, comparacao, quadro, lancamento, grafico, depois, podeInterpretar, congelado,
  frase, children, r,
}: MolduraDaRepeticaoProps<F> & { r: UseRepeticao<F> }) {
  const [adotando, setAdotando] = useState(false);
  const [desistindo, setDesistindo] = useState(false);
  const [abrindo, setAbrindo] = useState(false);

  if (!podeInterpretar) return <>{children}</>;
  const d = r.dados;
  const aberta = d?.aberta ?? null;
  /* O erro da ação aparece num lugar só: na janela aberta, se houver uma;
     senão, aqui. */
  const erroAqui = adotando || desistindo ? null : r.erro;

  if (!aberta) {
    return (
      <>
        {r.adotada != null && (
          <Aviso tipo="ok" titulo={`Tentativa ${r.adotada} adotada.`}>
            O laudo passa a usar o resultado dela. A tentativa anterior e o motivo
            ficam em «Tentativas anteriores» e na trilha; não vão ao PDF.
          </Aviso>
        )}
        <ErroDaApi erro={erroAqui} />
        {children}
        {d && !congelado && (
          <div className="repeticao-oferta">
            <p className="sub">
              Repetir abre uma nova tentativa aqui mesmo: você lança os pontos novos e vê
              os dois resultados lado a lado. O laudo só muda se você adotar a repetição.
            </p>
            <button type="button" className="botao secundario"
              aria-disabled={abrindo}
              onClick={async () => {
                if (abrindo) return;
                setAbrindo(true);
                try { await r.abrir(); } finally { setAbrindo(false); }
              }}>
              {abrindo ? 'Abrindo…' : 'Repetir o ensaio aqui'}
            </button>
          </div>
        )}
      </>
    );
  }
  if (!d?.atual) return <Carregando texto="Lendo a repetição" />;

  const trava = d.pendenciasDaRepeticao.find((p) => p.trava);
  /* «Provisório enquanto houver pendência» (spec §4). */
  const provisorio = d.pendenciasDaRepeticao.length > 0;
  const porqueNao = d.podeAdotar ? null
    : trava?.texto ?? 'A repetição ainda não pode ser adotada nesta situação do ensaio.';
  const c = comparacao?.(d.inicial, d.atual) ?? [];
  const comps = Array.isArray(c) ? c : [c];
  const comp = comps[0] ?? null;
  const fecharAdotar = () => { setAdotando(false); r.limparErro(); };
  const fecharDesistir = () => { setDesistindo(false); r.limparErro(); };

  return (
    <div className="repeticao">
      {aberta.abertaPor && (
        <p className="sub repeticao-topo">
          Tentativa {aberta.numero} aberta por {aberta.abertaPor}.
        </p>
      )}
      <Aviso tipo="atencao">
        <div className="repeticao-faixa">
          <span>
            <b>Repetindo o ensaio aqui mesmo.</b> {frase ?? <>Os pontos novos entram no
            gráfico assim que a umidade sai. O laudo continua com o resultado inicial até
            você adotar a repetição.</>}
          </span>
          <button type="button" className="botao secundario"
            onClick={() => { r.limparErro(); setDesistindo(true); }}>
            Desistir da repetição
          </button>
        </div>
      </Aviso>
      <ErroDaApi erro={erroAqui} />

      {comps.map((x, i) => (
        <ComparacaoDaRepeticao key={x.chave ?? i} vigente={d.vigente} numero={aberta.numero}
          provisorio={provisorio} comparacao={x} />
      ))}
      {desenhar(quadro, r)}

      <div className={`repeticao-duas${grafico == null ? ' repeticao-uma' : ''}`}>
        <div className="repeticao-lancamento">{desenhar(lancamento, r)}</div>
        {grafico != null && <div className="repeticao-grafico">{desenhar(grafico, r)}</div>}
      </div>
      {desenhar(depois, r)}

      <div className="repeticao-adotar">
        <p className="sub" id="repeticao-porque">{porqueNao ?? 'Pronto para adotar.'}</p>
        <button type="button" className="botao"
          aria-disabled={porqueNao !== null}
          aria-describedby="repeticao-porque"
          title={porqueNao ?? undefined}
          onClick={() => { if (!porqueNao) { r.limparErro(); setAdotando(true); } }}>
          Adotar a repetição
        </button>
      </div>

      <JanelaDeAdotar aberta={adotando} numero={aberta.numero} titulo={titulo}
        resumo={comp} aoFechar={fecharAdotar}
        aoAdotar={async (motivo) => {
          const ok = await r.adotar(motivo);
          if (ok) setAdotando(false);
          return ok;
        }} erro={r.erro} />
      <JanelaDeDesistir aberta={desistindo} numero={aberta.numero}
        aoFechar={fecharDesistir}
        aoDesistir={async (motivo) => {
          const ok = await r.desistir(motivo);
          if (ok) setDesistindo(false);
          return ok;
        }} erro={r.erro} />
    </div>
  );
}

/**
 * As linhas da repetição aberta, para as etapas de bancada (R2): com a
 * repetição aberta, a Pesagem úmida e a Pesagem seca listam e numeram as
 * linhas DELA (o `atual` do GET /repeticao), e não as da vigente — que ficam
 * à vista na Interpretação, como «Inicial». `versao` muda a cada gravação da
 * ficha: a lista relê.
 */
export function useLinhasDaRepeticao<F = any>(
  ensaioId: string, ficha: FichaDaRepeticao, aberta: number | null | undefined, versao: number,
): { atual: F | null; carregando: boolean } {
  const [atual, setAtual] = useState<F | null>(null);
  const [lida, setLida] = useState<number | null>(null);
  useEffect(() => {
    if (aberta == null) { setAtual(null); setLida(null); return undefined; }
    let vivo = true;
    verRepeticao(ensaioId, ficha).then((x) => {
      if (!vivo) return;
      setAtual((x.atual ?? null) as F | null);
      setLida(aberta);
    }).catch(() => { if (vivo) setLida(aberta); });
    return () => { vivo = false; };
  }, [ensaioId, ficha, aberta, versao]);
  return { atual, carregando: aberta != null && lida !== aberta };
}

// ══ as janelas ════════════════════════════════════════════════════════

/** O mínimo do motivo que vai à trilha (P-TXT-10; o banco confere de novo). */
export const MINIMO_DO_MOTIVO = 10;

function JanelaDeAdotar({ aberta, numero, titulo, resumo, aoFechar, aoAdotar, erro }: {
  aberta: boolean; numero: number; titulo: string; resumo: Comparacao | null;
  aoFechar(): void; aoAdotar(motivo: string): Promise<boolean>; erro: unknown;
}) {
  const [motivo, setMotivo] = useState('');
  const [gravando, setGravando] = useState(false);
  useEffect(() => { if (aberta) setMotivo(''); }, [aberta]);
  const n = motivo.trim().length;
  const curto = n < MINIMO_DO_MOTIVO;
  return (
    <Dialogo aberto={aberta} titulo={`Adotar a tentativa ${numero}?`}
      rotulo={titulo} aoFechar={aoFechar}>
      <p>
        {resumo
          ? <>O laudo passa de <b>{resumo.inicial.valor}{resumo.inicial.unidade
            ? ` ${resumo.inicial.unidade}` : ''}</b> para <b>{resumo.atual.valor}
            {resumo.atual.unidade ? ` ${resumo.atual.unidade}` : ''}</b>{resumo.rotulo
            ? ` (${resumo.rotulo})` : ''}.</>
          : <>O laudo passa a usar o resultado da tentativa {numero}.</>} A tentativa
        anterior sai da conta e do laudo e fica no histórico e na trilha, com o motivo.
      </p>
      <Area rotulo="Motivo (vai para a trilha, não vai ao laudo)" rows={3}
        value={motivo} onChange={(e) => setMotivo(e.target.value)}
        dica={`Mínimo de ${MINIMO_DO_MOTIVO} caracteres · ${n} digitados`} />
      {aberta && <ErroDaApi erro={erro} />}
      <div className="rodape-dialogo">
        <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="botao" aria-disabled={curto || gravando}
          title={curto ? `O motivo pede pelo menos ${MINIMO_DO_MOTIVO} caracteres.` : undefined}
          onClick={async () => {
            if (curto || gravando) return;
            setGravando(true);
            try { await aoAdotar(motivo); } finally { setGravando(false); }
          }}>
          {gravando ? 'Adotando…' : 'Adotar'}
        </button>
      </div>
    </Dialogo>
  );
}

function JanelaDeDesistir({ aberta, numero, aoFechar, aoDesistir, erro }: {
  aberta: boolean; numero: number; aoFechar(): void;
  aoDesistir(motivo: string): Promise<boolean>; erro: unknown;
}) {
  const [motivo, setMotivo] = useState('');
  const [gravando, setGravando] = useState(false);
  useEffect(() => { if (aberta) setMotivo(''); }, [aberta]);
  /* Opcional; escrito, vai à trilha e pede o mínimo (P-TXT-10). */
  const n = motivo.trim().length;
  const curto = n > 0 && n < MINIMO_DO_MOTIVO;
  return (
    <Dialogo aberto={aberta} titulo="Desistir da repetição?"
      rotulo={`Tentativa ${numero}`} aoFechar={aoFechar}>
      <p>
        A tentativa {numero} é encerrada sem mudar nada no laudo. Os pontos dela não se
        apagam: ficam em «Tentativas anteriores» e na trilha.
      </p>
      <Area rotulo="Motivo (opcional; vai para a trilha)" rows={2} value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        dica={`Opcional; se escrever, mínimo de ${MINIMO_DO_MOTIVO} caracteres · ${n} digitados`} />
      {aberta && <ErroDaApi erro={erro} />}
      <div className="rodape-dialogo">
        <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="botao" aria-disabled={curto || gravando}
          title={curto ? `Escrito, o motivo pede pelo menos ${MINIMO_DO_MOTIVO} caracteres.` : undefined}
          onClick={async () => {
            if (curto || gravando) return;
            setGravando(true);
            try { await aoDesistir(motivo); } finally { setGravando(false); }
          }}>
          {gravando ? 'Encerrando…' : 'Desistir da repetição'}
        </button>
      </div>
    </Dialogo>
  );
}
